import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  IconButton,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';

type DayType =
  | 'Sunday'
  | 'Monday'
  | 'Tuesday'
  | 'Wednesday'
  | 'Thursday'
  | 'Friday'
  | 'Saturday';

interface PeakHourEntry {
  startTime: string;
  endTime: string;
  amount: string;
}

interface PeakHoursData {
  sunday: PeakHourEntry[];
  monday: PeakHourEntry[];
  tuesday: PeakHourEntry[];
  wednesday: PeakHourEntry[];
  thursday: PeakHourEntry[];
  friday: PeakHourEntry[];
  saturday: PeakHourEntry[];
}

interface PeakHoursComponentProps {
  value: PeakHoursData;
  onChange: (value: PeakHoursData) => void;
  error?: string;
  touched?: boolean;
}

const days: DayType[] = [
  'Sunday',
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
];

const generateTimeOptions = () => {
  const times = [];
  for (let hours = 0; hours < 24; hours++) {
    for (let minutes = 0; minutes < 60; minutes += 30) {
      const hour = hours.toString().padStart(2, '0');
      const minute = minutes.toString().padStart(2, '0');
      times.push(`${hour}:${minute}`);
    }
  }
  // Add 23:59 as an additional option
  if (!times.includes('23:59')) {
    times.push('23:59');
  }
  return times;
};

// Convert local time (HH:MM) to UTC time format (HH:MM:00Z)
const convertLocalTimeToUTC = (localTime: string): string => {
  if (!localTime || localTime === '00:00') {
    return '00:00:00Z';
  }

  try {
    // Parse the local time (HH:MM format)
    const [hours, minutes] = localTime.split(':').map(Number);
    
    // Create a date object with today's date and the local time
    const localDate = new Date();
    localDate.setHours(hours, minutes || 0, 0, 0);
    
    // Get UTC hours and minutes
    const utcHours = localDate.getUTCHours().toString().padStart(2, '0');
    const utcMinutes = localDate.getUTCMinutes().toString().padStart(2, '0');
    
    // Return in format HH:MM:00Z
    return `${utcHours}:${utcMinutes}:00Z`;
  } catch (error) {
    console.error('Error converting local time to UTC:', error);
    // Fallback to original format if conversion fails
    return `${localTime}:00Z`;
  }
};

// Convert UTC time format (HH:MM:00Z or HH:MM) to local time (HH:MM) for display
const convertUTCToLocalTime = (utcTime: string): string => {
  if (!utcTime) {
    return '00:00';
  }

  try {
    // Remove Z suffix and extract time part (handle both "HH:MM:00Z" and "HH:MM" formats)
    const timeStr = utcTime.replace(':00Z', '').replace('Z', '');
    const [hours, minutes] = timeStr.split(':').map(Number);
    
    // Create a date object with today's date and the UTC time
    const utcDate = new Date();
    utcDate.setUTCHours(hours, minutes || 0, 0, 0);
    
    // Get local hours and minutes
    const localHours = utcDate.getHours().toString().padStart(2, '0');
    const localMinutes = utcDate.getMinutes().toString().padStart(2, '0');
    
    // Return in format HH:MM
    return `${localHours}:${localMinutes}`;
  } catch (error) {
    console.error('Error converting UTC time to local:', error);
    // Fallback: remove Z suffix if present
    return utcTime.replace(':00Z', '').replace('Z', '');
  }
};

// Validation function to check if both start and end times are 00:00
const validatePeakHourTimes = (startTime: string, endTime: string): string | null => {
  const startTimeDisplay = convertUTCToLocalTime(startTime);
  const endTimeDisplay = convertUTCToLocalTime(endTime);
  
  if (startTimeDisplay === '00:00' && endTimeDisplay === '00:00') {
    return 'Start and end time cannot both be 00:00';
  }
  
  return null;
};

const PeakHoursComponent: React.FC<PeakHoursComponentProps> = ({
  value,
  onChange,
  error,
  touched,
}) => {
  const [disabledDays, setDisabledDays] = useState<Record<DayType, boolean>>({
    Sunday: false,
    Monday: false,
    Tuesday: false,
    Wednesday: false,
    Thursday: false,
    Friday: false,
    Saturday: false,
  });

  // Sync disabled days with actual data
  useEffect(() => {
    const newDisabledDays: Record<DayType, boolean> = {
      Sunday: false,
      Monday: false,
      Tuesday: false,
      Wednesday: false,
      Thursday: false,
      Friday: false,
      Saturday: false,
    };

    Object.entries(value).forEach(([day, hours]) => {
      const dayCapitalized = (day.charAt(0).toUpperCase() + day.slice(1)) as DayType;
      if (!hours || hours.length === 0) {
        newDisabledDays[dayCapitalized] = true;
      }
    });

    setDisabledDays(newDisabledDays);
  }, [value]);

  const timeOptions = generateTimeOptions();

  const handleDisableDay = (day: DayType) => {
    const newDisabledState = !disabledDays[day];
    setDisabledDays(prev => ({ ...prev, [day]: newDisabledState }));

    const dayKey = day.toLowerCase() as keyof PeakHoursData;
    const newValue = { ...value };

    if (newDisabledState) {
      // If disabling the day, clear the peak hours
      newValue[dayKey] = [];
    } else {
      // If enabling the day, add one empty peak hour
      newValue[dayKey] = [{ startTime: '00:00:00Z', endTime: '00:00:00Z', amount: '0' }];
    }

    onChange(newValue);
  };

  const handleAddPeakHour = (day: DayType) => {
    const dayKey = day.toLowerCase() as keyof PeakHoursData;
    const currentHours = value[dayKey] || [];
    
    if (currentHours.length < 5) {
      const newValue = { ...value };
      newValue[dayKey] = [...currentHours, { startTime: '00:00:00Z', endTime: '00:00:00Z', amount: '0' }];
      onChange(newValue);
    }
  };

  const handleRemovePeakHour = (day: DayType, index: number) => {
    const dayKey = day.toLowerCase() as keyof PeakHoursData;
    const currentHours = value[dayKey] || [];
    
    const newValue = { ...value };
    newValue[dayKey] = currentHours.filter((_, i) => i !== index);
    onChange(newValue);
  };

  const handlePeakHourFieldChange = (
    day: DayType,
    index: number,
    fieldName: 'startTime' | 'endTime' | 'amount',
    fieldValue: string
  ) => {
    const dayKey = day.toLowerCase() as keyof PeakHoursData;
    const currentHours = value[dayKey] || [];
    
    if (currentHours[index]) {
      const newValue = { ...value };
      const updatedHour = { ...currentHours[index] };
      
      if (fieldName === 'startTime' || fieldName === 'endTime') {
        const formattedTime = fieldValue ? convertLocalTimeToUTC(fieldValue) : '00:00:00Z';
        updatedHour[fieldName] = formattedTime;
      } else if (fieldName === 'amount') {
        updatedHour.amount = fieldValue || '0';
      }
      
      newValue[dayKey] = [...currentHours];
      newValue[dayKey][index] = updatedHour;
      onChange(newValue);
    }
  };

  return (
    <Box>
      {error && touched && (
        <Typography sx={{ color: 'red', fontSize: '12px', mb: 1 }}>
          {error}
        </Typography>
      )}
      
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1, 
        mb: 3, 
        p: 2, 
        backgroundColor: '#e3f2fd', 
        borderRadius: 1,
        border: '1px solid #bbdefb'
      }}>
        <Typography sx={{ color: '#1976d2', fontSize: '14px' }}>
          💡
        </Typography>
        <Typography sx={{ color: '#1976d2', fontSize: '14px', fontWeight: 500 }}>
          Tip: You can add up to 5 peak hours per day. Configure time periods when demand is high.
        </Typography>
      </Box>

      {days.map((day) => {
        const dayKey = day.toLowerCase() as keyof PeakHoursData;
        const dayHours = value[dayKey] || [];
        
        return (
          <Box
            key={day}
            sx={{
              mb: 3,
              p: 3,
              backgroundColor: 'white',
              borderRadius: 2,
              border: '1px solid #e0e0e0',
              boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
              '&:hover': {
                boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
                transition: 'box-shadow 0.2s ease-in-out'
              }
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between', 
              mb: 2 
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography sx={{
                  fontWeight: 600,
                  fontSize: '16px',
                  color: '#2c3e50',
                  mr: 2
                }}>
                  {day}
                </Typography>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={disabledDays[day]}
                      onChange={() => handleDisableDay(day)}
                      sx={{ 
                        p: 0,
                        '&.Mui-checked': {
                          color: '#f44336'
                        }
                      }}
                    />
                  }
                  label={
                    <Typography sx={{ 
                      fontSize: '14px', 
                      color: disabledDays[day] ? '#f44336' : '#6c757d',
                      fontWeight: disabledDays[day] ? 600 : 400
                    }}>
                      {disabledDays[day] ? 'Disabled' : 'Enable Peak Hours'}
                    </Typography>
                  }
                />
              </Box>
              
              {!disabledDays[day] && (
                <Button
                  variant="outlined"
                  onClick={() => handleAddPeakHour(day)}
                  disabled={dayHours.length >= 5}
                  startIcon={<Typography>+</Typography>}
                  sx={{
                    textTransform: 'none',
                    borderColor: '#4caf50',
                    color: '#4caf50',
                    '&:hover': {
                      borderColor: '#45a049',
                      backgroundColor: '#f1f8e9'
                    },
                    '&:disabled': {
                      borderColor: '#ccc',
                      color: '#ccc'
                    }
                  }}
                >
                  Add Peak Hour
                </Button>
              )}
            </Box>

            {!disabledDays[day] && (
              <Box>
                {dayHours.map((hour, idx) => {
                  const timeValidationError = validatePeakHourTimes(hour.startTime, hour.endTime);
                  
                  return (
                    <Box
                      key={`${day}-${idx}`}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 2,
                        mb: 2,
                        p: 2,
                        backgroundColor: idx === 0 ? '#f8f9fa' : '#fff3e0',
                        borderRadius: 1,
                        border: `1px solid ${idx === 0 ? '#e9ecef' : '#ffcc80'}`,
                        position: 'relative'
                      }}
                    >
                      {idx > 0 && (
                        <Box sx={{ 
                          position: 'absolute', 
                          top: '-8px', 
                          left: '16px', 
                          backgroundColor: '#ff9800',
                          color: 'white',
                          px: 1,
                          py: 0.5,
                          borderRadius: 1,
                          fontSize: '12px',
                          fontWeight: 600
                        }}>
                          Peak Hour {idx + 1}
                        </Box>
                      )}

                      <Box sx={{ minWidth: '120px' }}>
                        <Typography sx={{ fontSize: '14px', color: '#6c757d', mb: 0.5 }}>
                          Start Time
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={convertUTCToLocalTime(hour.startTime)}
                            onChange={(e) =>
                              handlePeakHourFieldChange(
                                day,
                                idx,
                                'startTime',
                                e.target.value
                              )
                            }
                            displayEmpty
                            size="small"
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 200,
                                },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: timeValidationError ? '#d32f2f' : '#ddd',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: timeValidationError ? '#d32f2f' : '#4caf50',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: timeValidationError ? '#d32f2f' : '#4caf50',
                              },
                            }}
                          >
                            <MenuItem value="">
                              <em>Select Start Time</em>
                            </MenuItem>
                            {timeOptions.map((time) => (
                              <MenuItem key={time} value={time}>
                                {time}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {timeValidationError && (
                          <Typography sx={{ color: '#d32f2f', fontSize: '12px', mt: 0.5 }}>
                            {timeValidationError}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ minWidth: '120px' }}>
                        <Typography sx={{ fontSize: '14px', color: '#6c757d', mb: 0.5 }}>
                          End Time
                        </Typography>
                        <FormControl fullWidth>
                          <Select
                            value={convertUTCToLocalTime(hour.endTime)}
                            onChange={(e) =>
                              handlePeakHourFieldChange(
                                day,
                                idx,
                                'endTime',
                                e.target.value
                              )
                            }
                            displayEmpty
                            size="small"
                            MenuProps={{
                              PaperProps: {
                                style: {
                                  maxHeight: 200,
                                },
                              },
                            }}
                            sx={{
                              '& .MuiOutlinedInput-notchedOutline': {
                                borderColor: timeValidationError ? '#d32f2f' : '#ddd',
                              },
                              '&:hover .MuiOutlinedInput-notchedOutline': {
                                borderColor: timeValidationError ? '#d32f2f' : '#4caf50',
                              },
                              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                borderColor: timeValidationError ? '#d32f2f' : '#4caf50',
                              },
                            }}
                          >
                            <MenuItem value="">
                              <em>Select End Time</em>
                            </MenuItem>
                            {timeOptions.map((time) => (
                              <MenuItem key={time} value={time}>
                                {time}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        {timeValidationError && (
                          <Typography sx={{ color: '#d32f2f', fontSize: '12px', mt: 0.5 }}>
                            {timeValidationError}
                          </Typography>
                        )}
                      </Box>

                      <Box sx={{ minWidth: '120px' }}>
                        <Typography sx={{ fontSize: '14px', color: '#6c757d', mb: 0.5 }}>
                          Additional Fee (JOD)
                        </Typography>
                        <TextField
                          value={hour.amount.toString()}
                          onChange={(e) => {
                            let value = e.target.value;
                            if (value === '' || isNaN(Number(value))) value = '0';
                            if (Number(value) < 0) value = '0';
                            handlePeakHourFieldChange(day, idx, 'amount', value);
                          }}
                          placeholder="0.00"
                          size="small"
                          variant="outlined"
                          fullWidth
                          inputProps={{ min: 0, step: 0.01 }}
                          sx={{
                            '& .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#ddd',
                            },
                            '&:hover .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4caf50',
                            },
                            '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                              borderColor: '#4caf50',
                            },
                          }}
                        />
                      </Box>

                      {idx > 0 && (
                        <IconButton
                          onClick={() => handleRemovePeakHour(day, idx)}
                          sx={{
                            color: '#f44336',
                            '&:hover': {
                              backgroundColor: '#ffebee'
                            }
                          }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default PeakHoursComponent; 